import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";

export default function Sessions({ user }) {
  const [sessions, setSessions] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [newSessionName, setNewSessionName] = useState("");
  const navigate = useNavigate();
  const apiUrl = `${process.env.REACT_APP_API_URL}/sessions`;

  const [hoverDelete, setHoverDelete] = useState(null);

  useEffect(() => {
    AOS.init({ duration: 1200, once: false }); // Désactiver le "once" pour garantir un rendu dynamique
  });

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const response = await axios.get(apiUrl, {
          headers: { Authorization: `Bearer ${user.access_token}` },
        });

        setSessions(response.data);
        setIsLoaded(true); // Attendre que les sessions soient bien chargées
      } catch (error) {
        console.error("Erreur lors du chargement des sessions :", error);
        setSessions([]);
      }
    };

    if (user) {
      fetchSessions();
    }
  }, [user]);

  useEffect(() => {
    if (isLoaded) {
      setTimeout(() => AOS.refresh(), 300); // Rafraîchir AOS une fois les sessions bien chargées
    }
  }, [isLoaded]);

  const handleSessionClick = (sessionId) => {
    navigate(`/sessions/${sessionId}`);
  };

  const handleCreateSession = async () => {
    if (!newSessionName.trim()) return;

    try {
      const response = await axios.post(
        apiUrl,
        { name: newSessionName },
        { headers: { Authorization: `Bearer ${user.access_token}` } }
      );

      setSessions([...sessions, response.data]);
      setNewSessionName("");
    } catch (error) {
      console.error("Erreur lors de la création de la session :", error);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await axios.delete(`${apiUrl}/${sessionId}`, {
        headers: { Authorization: `Bearer ${user.access_token}` },
      });

      setSessions(sessions.filter((session) => session.id !== sessionId));
    } catch (error) {
      console.error("Erreur lors de la suppression de la session :", error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleCreateSession();
    }
  };

  return (
    <div style={pageStyle}>
      {/* Bandeau en haut */}
      <div style={headerStyle}>
        <span style={logoStyle} onClick={() => navigate("/")} role="button">
          Dansy
        </span>
        <h1 style={titleStyle}>Mes Sessions</h1>
      </div>
  
      {/* Contenu principal */}
      <div style={contentContainerStyle}>
        <div style={scrollableAreaStyle}>
          {sessions === null ? (
            <div style={sessionsListStyle}>
              {[...Array(6)].map((_, index) => (
                <div key={index} style={sessionSkeletonStyle}></div>
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <p style={emptyMessageStyle}>Aucune session disponible pour le moment.</p>
          ) : (
            <div key={sessions?.length} style={sessionsListStyle}>
              {sessions.map((session) => (
                <div
                  key={session.id}
                  style={sessionItemStyle}
                  onClick={() => handleSessionClick(session.id)}
                  data-aos="fade-up"
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f8f8")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                >
                  <span
                    title={session.name} // Tooltip au survol
                    style={sessionNameStyle}
                    onClick={() => handleSessionClick(session.id)}
                  >
                    {session.name}
                  </span>
                  <span style={sessionDetailsStyle}>
                    Créée le {new Date(session.created_at).toLocaleDateString()} |  
                    Danseurs: {session.dancers?.length || 0} |  
                    Périodes: {session.periods?.length || 0}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSession(session.id);
                    }}
                    onMouseEnter={() => setHoverDelete(session.id)}
                    onMouseLeave={() => setHoverDelete(null)}
                    style={{
                      ...deleteButtonStyle,
                      backgroundColor: hoverDelete === session.id ? "#e5e5e5" : "transparent",
                      transition: "background-color 0.4s ease",
                      zIndex: 2,
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
  
      {/* Champ de création de session */}
      <div style={createSessionContainerStyle}>
        <input
          type="text"
          placeholder="Nom de la nouvelle session"
          value={newSessionName}
          onChange={(e) => setNewSessionName(e.target.value)}
          onKeyDown={handleKeyDown}
          style={inputStyle}
        />
        <button onClick={handleCreateSession} style={addButtonStyle}>+</button>
      </div>
    </div>
  );
  
}

/* === Styles === */


const pageStyle = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  overflowX: "hidden",
  position: "relative",
  minHeight: "100vh",
  backgroundColor: "#fff",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
};

const headerStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, #fff0f3, #ffd6dd)",
  color: "#000",
  padding: "15px",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
  height: "30px",
  zIndex: 100,
};

const logoStyle = {
  position: "absolute",
  left: "20px",
  fontSize: "1.7rem",
  fontFamily: "'Shirkhand', cursive",
  color: "#ff477e",
  fontWeight: "bold",
  cursor: "pointer",
};

const titleStyle = {
  fontSize: "1.5rem",
  fontWeight: 700,
};

const contentContainerStyle = {
  marginTop: "0px",
  width: "100%",
  height: "calc(100vh - 70px)", // Prend toute la hauteur disponible
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-between", // Répartit l'espace entre la liste et le formulaire
};

const scrollableAreaStyle = {
  width: "100%",
  minHeight: "50vh",
  maxHeight: "calc(100vh - 120px)", // Ajustement pour éviter que le contenu déborde
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px 0",
  overflowY: "auto", // Activation du scroll si nécessaire
  WebkitOverflowScrolling: "touch", // Optimisation mobile
};

const emptyMessageStyle = {
  fontSize: "1.2rem",
  color: "#666",
  marginTop: "20px",
};

const sessionsListStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "15px",
  width: "80%",
  maxWidth: "600px",
  marginTop: "60px",
  marginBottom: "80px", // Augmenté pour éviter d’être masqué
};

const sessionItemStyle = {
  backgroundColor: "#fff",
  height: "30px", // Hauteur fixe
  padding: "15px 20px",
  borderRadius: "15px",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: "1.2rem",
  transition: "background-color 0.3s ease",
  cursor: "pointer",
  margin: "0px 0", // Espacement égal à celui des skeletons
  width: "100%", // Assure un bon alignement
  gap: "10px", // Espacement entre les éléments
};

const sessionSkeletonStyle = {
  backgroundColor: "#eee",
  height: "60px", // Hauteur alignée avec sessionItemStyle
  borderRadius: "15px",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  animation: "pulse 1.5s infinite",
  margin: "0px 0", // Assure le même espacement que sessionItemStyle
  width: "100%", // Assure l'alignement correct
  display: "flex", // Simule le comportement des éléments chargés
  alignItems: "center", // Alignement du contenu simulé
  justifyContent: "center", // Centrage du chargement
};


const sessionNameStyle = {
  fontWeight: 600,
  color: "#ff477e",
  textAlign: "left",
  flex: "1",
  cursor: "pointer",
  whiteSpace: "nowrap",      // Empêche le passage à la ligne
  overflow: "hidden",        // Cache le débordement
  textOverflow: "ellipsis",  // Ajoute "..." si le texte est trop long
  maxWidth: "150px",         // Ajuste la largeur maximale (à adapter si besoin)
};

const sessionDetailsStyle = {
  fontSize: "0.8rem",
  color: "#666",
  textAlign: "right",
  flex: "3",
  whiteSpace: "nowrap",       // Empêche le passage à la ligne
  overflow: "hidden",         // Cache le débordement
  textOverflow: "ellipsis",   // Tronque si nécessaire
  maxWidth: "300px",          // Empêche d'occuper toute la largeur
};

const deleteButtonStyle = {
  background: "none",
  border: "none",
  fontSize: "1.5rem",
  cursor: "pointer",
  color: "#aaa",
  marginLeft: "5px",
  width: "32px", // Définit une largeur fixe
  height: "32px", // Définit une hauteur fixe
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%", // Transforme en cercle
  transition: "background-color 0.2s ease", // Animation fluide
  zIndex: 2, // Place le bouton au-dessus des autres éléments
  // Effet au survol
  ":hover": {
    backgroundColor: "#D2042D", // Couleur de fond grise au survol
  },
};

const createSessionContainerStyle = {
  width: "100%",
  maxWidth: "none",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "15px 20px", // Ajout de padding pour éviter un effet trop large
  background: "linear-gradient(135deg, #fff0f3, #ffd6dd)",
  position: "fixed",
  bottom: "0",
  left: "0",
  right: "0",
  boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
  zIndex: 100,
  justifyContent: "center", // Centre les éléments pour éviter qu'ils prennent toute la largeur
};

const inputStyle = {
  flex: "0 1 500px", // Largeur limitée à 300px, ne grandit pas trop
  padding: "12px",
  border: "1px solid #ddd",
  borderRadius: "50px",
  fontSize: "16px",
  maxWidth: "600px", // Empêche qu'il s'étende trop
};

const addButtonStyle = {
  width: "40px", // Réduit légèrement la taille
  height: "40px",
  fontSize: "1.2rem", // Réduit la taille de la police
  backgroundColor: "#ff477e",
  color: "#fff",
  border: "none",
  borderRadius: "50%",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0, // Empêche le bouton de rétrécir
};